/**
 * Data layer for the COMPANY panel's Shoorah Videos section.
 *
 * Covers both kinds of video in one place, because they live in one collection
 * and a company admin thinks of them as one library:
 *   Health Videos — NHS / guide videos, embedded from Vimeo
 *   Shoorah Vids  — uploaded through the Shoorah admin panel
 *
 * Backed by the company/v1 vids-hub endpoints (token-scoped to the company; the
 * B2B/Members toggle is appended by api/companyApi.js). Every fetcher takes an
 * optional `department` so the section's filter can narrow the whole page.
 *
 * The adapter is exported because the super-admin Vids Hub is served by the
 * same builders in services/vidsHubService.js and should adapt the payload
 * through here rather than restating the field mapping.
 */

import {
  getCompanyVidsDashboard,
  getCompanyVidsUsers,
  getCompanyVidsUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Watch-record block shared by every vids dashboard. */
export const adaptVidsWatching = (data) => ({
  viewers: data.viewers,
  totalViews: data.totalViews,
  videosWatched: data.videosWatched ?? 0,
  totalWatchTimeMins: data.totalWatchTimeMins,
  avgWatchMins: data.avgWatchMins,
  avgVideosPerViewer: data.avgVideosPerViewer,
  completions: data.completions ?? 0,
  completionRate: data.completionRate ?? 0,
  likes: data.likes ?? 0,
  shares: data.shares ?? 0,
  mostWatchedVideo: data.mostWatchedVideo,
  topTenSharePercentage: data.topTenSharePercentage,
  mostWatched: (data.mostWatched || []).map((video) => ({
    id: video._id,
    name: video.name,
    kind: video.kind,
    viewers: video.viewers,
    completions: video.completions ?? 0,
    watchMins: video.watchMins,
    rating: video.rating ?? null,
    expert: video.expert || null
  })),
  // Backend zero-fills the last 30 days; charts consume plain series.
  viewsTrend: (data.viewsTrend || []).map((day) => day.views),
  watchMinsTrend: (data.viewsTrend || []).map((day) => day.watchMins || 0),
  weekdayViews: data.weekdayViews || [],
  progressSplit: data.progressSplit || [],
  watchLengthDistribution: data.watchLengthDistribution || [],
  viewerDepth: data.viewerDepth || [],
  kindBreakdown: data.kindBreakdown || [],
  topExperts: data.topExperts || [],
  topCategories: data.topCategories || []
});

export const fetchCompanyVidsDashboard = async (department = '') => {
  try {
    const data = unwrap(await getCompanyVidsDashboard(department));
    return {
      ...adaptVidsWatching(data),
      // Company-only: the size of the population, so the page can say
      // "32 of 240 employees have watched".
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyVidsDashboard error:', error);
    return null;
  }
};

// Matches the VidsUsersTable fetcher contract.
export const fetchCompanyVidsUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyVidsUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanyVidsUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's video profile ({ user, mostWatchedVideo, topVideos,
 * watchHistory }). The backend 404s any user outside this company, so no
 * scoping is needed here.
 */
export const fetchCompanyVidsUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanyVidsUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanyVidsUserDetail error:', error);
    return null;
  }
};
